/**
 * ReferViewModel — 参照视图模型
 *
 * ## 架构角色
 * 提供参照弹窗的生命周期与事件扩展能力。继承 BaseModel。
 * 重点：确保事件名和触发时机正确，事件触发用 this.execute('eventName', args)。
 *
 * ## 事件（11 个，通过 on/execute 触发）
 * | 事件 | 触发时机 |
 * |------|---------|
 * | beforeBrowse | 点击按钮弹出参照前；一般在此给参照请求增加过滤参数 |
 * | afterInitVm | 参照弹窗模型初始化完毕；arg.vm 可处理参照表/树数据 |
 * | beforeValueChange | 参照值改变前置 |
 * | afterValueChange | 参照值改变后置 |
 * | beforeGetRefMeta | 调 getRefMeta 前置，可直接修改 param |
 * | beforeGetRefData | 调 getRefData 前置，可直接修改 param |
 * | afterOkClick | 点击确认框后回调；表/树表参照返回选中行数据，树参照返回选中节点 |
 * | getRefMetaReady | 请求完参照 meta 信息的回调 |
 * | beforeInsertRowFromRefer | （Refer 多选）子表/孙表多选参照插入行前置（第二条起触发） |
 * | beforeInsertRowsFromRefer | （Refer 多选）子表/孙表多选参照插入全部行前置（只触发一次） |
 * | 表格内参照 beforeBrowse | 参照模型透传到表格模型；arg: { rowIndex, cellName, context, carryParams } |
 *
 * ## beforeBrowse 设置过滤参数的方式
 * - this.setFilter(condition) → 拼接到 getRefData 参数 condition
 * - this.setCondition({ params1:111, params2:222 }) → 拼接到 getRefData 参数 mapCondition
 * - data.params1 = 111; data.params2 = 222; → 自定义参数放到最外层请求参数
 * - this.setTreeFilter(condition) → tree/treelist 参照过滤
 *
 * ## 使用示例
 * ```js
 * var rvm = new ttx.models.ReferViewModel({ cRefType: 'aae_user' });
 * rvm.on('beforeBrowse', function (arg) { this.setFilter({ isEnable: '1' }); });
 * rvm.browse();
 * rvm.on('afterOkClick', function (arg) { console.log(arg.selected); });
 * rvm.okClick(selectedRows);
 * ```
 */
ttx.models.register('ReferViewModel', function (modelType) {
  const referViewModel = function (data) {
    ttx.models.BaseModel.call(this, data);
  };
  referViewModel.prototype = ttx.utils.getPrototype(ttx.models.BaseModel.prototype);
  referViewModel.prototype.modelType = modelType;

  // ===== beforeBrowse 过滤参数设置 =====

  // 设置过滤条件（拼接到 getRefData 参数 condition）
  referViewModel.prototype.setFilter = function (condition) {
    this._set_data('filter', condition, true);
  };

  // 获取过滤条件
  referViewModel.prototype.getFilter = function () {
    return this._get_data('filter');
  };

  // 设置 condition（拼接到 getRefData 参数 mapCondition）
  referViewModel.prototype.setCondition = function (condition) {
    this._set_data('mapCondition', condition, true);
  };

  // 获取 condition
  referViewModel.prototype.getCondition = function () {
    return this._get_data('mapCondition');
  };

  // 设置 treeFilter（tree/treelist 参照过滤）
  referViewModel.prototype.setTreeFilter = function (condition) {
    this._set_data('treeFilter', condition, true);
  };

  // 获取 treeFilter
  referViewModel.prototype.getTreeFilter = function () {
    return this._get_data('treeFilter');
  };

  // 点击按钮弹出参照前；一般在此给参照请求增加过滤参数
  // 表格内参照透传到表格模型：arg: { rowIndex, cellName, context, carryParams }
  referViewModel.prototype.browse = function (args) {
    const param = args || {};
    // beforeBrowse 返回 false 中止
    if (!this.execute('beforeBrowse', param)) return null;
    return param;
  };

  // 参照弹窗模型初始化完毕；arg.vm 可处理参照表/树数据
  referViewModel.prototype.initVm = function (vm) {
    const arg = { vm: vm };
    if (!this.execute('afterInitVm', arg)) return null;
    this._set_data('referVm', vm, true);
    return vm;
  };

  // 调 getRefMeta 前置，可直接修改 param
  referViewModel.prototype.getRefMeta = function (param) {
    const arg = { param: param || {} };
    if (!this.execute('beforeGetRefMeta', arg)) return null;
    return arg.param;
  };

  // 请求完参照 meta 信息的回调
  referViewModel.prototype.onRefMetaReady = function (meta) {
    this._set_data('refMeta', meta, true);
    this.execute('getRefMetaReady', { meta: meta });
    return meta;
  };

  // 调 getRefData 前置，可直接修改 param
  // 拼接 beforeBrowse 设置的过滤参数：filter→condition、condition→mapCondition、treeFilter→treeFilter
  referViewModel.prototype.getRefData = function (param) {
    const arg = { param: param || {} };
    // 拼接 beforeBrowse 设置的过滤参数
    const filter = this.getFilter();
    if (filter) arg.param.condition = filter;
    const mapCondition = this.getCondition();
    if (mapCondition) arg.param.mapCondition = mapCondition;
    const treeFilter = this.getTreeFilter();
    if (treeFilter) arg.param.treeFilter = treeFilter;
    // beforeGetRefData 返回 false 中止
    if (!this.execute('beforeGetRefData', arg)) return null;
    return arg.param;
  };

  // 参照值改变前置
  referViewModel.prototype.setValue = function (value) {
    const oldValue = this._get_data('value');
    const data = { value: value, oldValue: oldValue };
    // beforeValueChange 返回 false 中止
    if (!this.execute('beforeValueChange', data)) return null;
    this._set_data('value', value, true);
    this.execute('afterValueChange', data);
    return value;
  };

  // 获取参照值
  referViewModel.prototype.getValue = function () {
    return this._get_data('value');
  };

  // 点击确认框后回调；表/树表参照返回选中行数据，树参照返回选中节点
  referViewModel.prototype.okClick = function (selected) {
    const arg = { selected: selected };
    if (!this.execute('afterOkClick', arg)) return null;
    return arg.selected;
  };

  // （Refer 多选）子表/孙表多选参照插入行前置（第二条起触发）
  referViewModel.prototype.insertRowFromRefer = function (rowIndex, rowData) {
    const arg = { rowIndex: rowIndex, rowData: rowData };
    // beforeInsertRowFromRefer 返回 false 中止该行插入
    if (!this.execute('beforeInsertRowFromRefer', arg)) return false;
    return true;
  };

  // （Refer 多选）子表/孙表多选参照插入全部行前置（只触发一次）
  referViewModel.prototype.insertRowsFromRefer = function (rowDatas) {
    const arg = { rowDatas: rowDatas };
    // beforeInsertRowsFromRefer 返回 false 中止全部插入
    if (!this.execute('beforeInsertRowsFromRefer', arg)) return false;
    return true;
  };

  return referViewModel;
});
